import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { CompanyAvatar, CompanyCard, companiesQuery } from '@/entities/company'
import type { Company } from '@/shared/api'
import { switchCompanyMutation } from '@/shared/api'
import { Icon, Popover, Skeleton, WidgetError } from '@/shared/ui'
import s from './CompanySwitcher.module.css'

type CompanySwitcherProps = {
  activeCompany: Company
  /**
   * Строка «Сервис «Компании»» (prototype/Prototype.dc.html:163) — слот, а не собственный
   * запрос фичи: URL сервиса приезжает из `entities/platform` (`PlatformService`), который
   * нужен ТРЁМ разным местам шапки (свитчер сервисов, эта строка, «Личный кабинет» в профиле).
   * Виджет уже вынужден звать его сам — дублировать вызов здесь незачем, а `features →
   * widgets` — импорт снизу вверх, FSD его запрещает.
   */
  footer?: ReactNode
}

/**
 * Свитчер компаний (prototype/Prototype.dc.html:125-166): триггер в шапке + поповер со списком.
 *
 * Список компаний — лениво, `enabled: open`: полный справочник нужен только когда поповер
 * реально открыт, тот же принцип, что и у ленты уведомлений. Активная компания приходит
 * снаружи (`sessionQuery` уже читает её на уровне `TopBar` ради устойчивости к сбоям) — так
 * триггер (аватар, имя, город · домен) рисуется мгновенно, не дожидаясь списка.
 */
export function CompanySwitcher({ activeCompany, footer }: CompanySwitcherProps) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const router = useRouter()

  const {
    data: companies,
    error,
    refetch,
  } = useQuery({ ...companiesQuery(), enabled: open })

  const { mutate: switchTo, isPending } = useMutation({
    ...switchCompanyMutation(),
    onSuccess: async () => {
      // Поповер закрываем ПЕРВЫМ: список компаний живёт с `enabled: open`, и сбрось мы кеш до
      // закрытия — он, ещё активный, тут же перезапросился бы, хотя через миг исчезнет с экрана.
      setOpen(false)

      // `resetQueries()`, а НЕ `clear()`, который предписан брифом.
      //
      // `clear()` удаляет запросы из кеша (`query.destroy()`), но НЕ заставляет уже
      // смонтированные `useQuery` перезапросить данные — а шапка смонтирована постоянно.
      // Обсервер подхватит новый Query только на следующем `setOptions()`, то есть на
      // ре-рендере владеющего компонента, которого после чистки кеша ничто не вызывает.
      // На практике это значило: шапка чистит кеш и **навсегда остаётся показывать старую
      // компанию**. Проверено зондом на живой библиотеке и живым прогоном в браузере.
      //
      // `resetQueries()` делает и то, и другое: обнуляет данные каждого запроса (значит чужая
      // компания не мелькнёт) И сам перезапрашивает все активные, не полагаясь на случайный
      // ре-рендер. После правки шапка показывает новую компанию на первом же кадре.
      await queryClient.resetQueries()
      await router.invalidate()
    },
  })

  const others = companies?.filter((company) => company.id !== activeCompany.id) ?? []

  return (
    <Popover
      align="start"
      width={298}
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className={s.trigger}>
          <CompanyAvatar company={activeCompany} size={24} />
          <span className={s.triggerText}>
            <span className={s.triggerName}>{activeCompany.name}</span>
            <span className={s.triggerDomain}>
              {activeCompany.city} · {activeCompany.domain}
            </span>
          </span>
          <Icon name="chevron-down" size={11} strokeWidth={2.4} className={s.chevron} />
        </button>
      }
    >
      <div className={s.header}>
        <span className={s.label}>Компания</span>
        {companies && <span className={s.count}>{companies.length}</span>}
      </div>

      <CompanyCard company={activeCompany} active />

      {/* Данные — первыми, ошибка — только если показать нечего. Упавший фоновый перезапрос
          оставляет `data` целой и одновременно выставляет `error`; подробности — в TopBar. */}
      {!companies ? (
        error ? (
          <div className={s.errorWrap}>
            <WidgetError error={error} retry={() => void refetch()} />
          </div>
        ) : (
          <div className={s.skeletons}>
            <Skeleton width="100%" height={48} radius={10} />
            <Skeleton width="100%" height={48} radius={10} />
          </div>
        )
      ) : others.length > 0 ? (
        <>
          <div className={s.otherLabel}>Другие компании</div>
          {others.map((company, index) => (
            <CompanyCard
              key={company.id}
              company={company}
              disabled={isPending}
              onClick={() => switchTo({ path: { companyId: company.id } })}
              // Макет (строки 147, 152, 157): .04s / .08s / .12s. База 40 мс, шаг 40 мс —
              // первая строка тоже с задержкой, а не мгновенно. Без базы весь каскад играл
              // на 40 мс раньше макета.
              style={{ animationDelay: `${40 + index * 40}ms` }}
            />
          ))}
        </>
      ) : null}

      {footer && (
        <>
          <div className={s.divider} />
          {footer}
        </>
      )}
    </Popover>
  )
}
